/**
 * Shared helpers for URL-facing string handles (e.g. usernames, season IDs).
 *
 * Each handle has a label, length bounds, and a character pattern. Both
 * `HoopsUsernameService` and `HoopsSeasonService` build their own validate/
 * normalize methods on top of `validate` and `normalize` here so that the
 * shape of the error messages and the lowercase-trim canonicalization stay
 * consistent across the app.
 */
export interface HoopsIdRules {
    /** Human-readable label used in error messages (e.g. "Username", "Season ID"). */
    readonly label: string;
    readonly minLength: number;
    readonly maxLength: number;
    /** Regex the normalized handle must fully match. */
    readonly charPattern: RegExp;
    /** Plain-English description of the allowed characters, used in error messages. */
    readonly charDescription: string;
}

/** Thrown by a deterministic-ID create (season, week, game day) when the ID is already taken. */
export class HoopsIdTakenError extends Error {
    constructor(label: string, id: string) {
        super(`${label} ID "${id}" is already taken`);
        this.name = 'HoopsIdTakenError';
    }
}

export class HoopsIdUtils {
    /**
     * Canonicalizes a raw handle (lowercase + trim). The result is what gets
     * stored in Firestore and used as the lookup key.
     */
    static normalize(raw: string): string {
        return raw.trim().toLowerCase();
    }

    /**
     * Validates a raw handle against `rules`. Returns an error message if
     * invalid, or `null` if valid.
     */
    static validate(raw: string, rules: HoopsIdRules): string | null {
        const normalized = this.normalize(raw);
        if (normalized.length === 0) return `${rules.label} is required`;
        if (normalized.length < rules.minLength) {
            return `${rules.label} must be at least ${rules.minLength} characters`;
        }
        if (normalized.length > rules.maxLength) {
            return `${rules.label} must be at most ${rules.maxLength} characters`;
        }
        if (!rules.charPattern.test(normalized)) {
            return `${rules.label} can only contain ${rules.charDescription}`;
        }
        return null;
    }

    /** Strips the `${seasonId}-` prefix a season-scoped composite ID carries, for use as a short URL segment. */
    static localSegment(id: string, seasonId: string): string {
        return id.startsWith(`${seasonId}-`) ? id.slice(seasonId.length + 1) : id;
    }

    /** Reconstitutes a season-scoped composite ID from its URL local segment (inverse of `localSegment`). */
    static compositeId(seasonId: string, localSegment: string): string {
        return `${seasonId}-${localSegment}`;
    }
}
